import { z } from "zod";
import { BLOCK_TYPE, BLOCK_TYPES, type BlockType } from "../enums/metrics";

export type { BlockType };
export { BLOCK_TYPES };

const optionalText = z.string().trim().min(1).nullable();
const score = z.number().int().min(0).max(100);

export const NoteBlockSchema = z.object({
  type: z.literal("note"),
  title: optionalText,
  body: z.string().trim().min(1),
});

export const EvidenceBlockSchema = z.object({
  type: z.literal("evidence"),
  title: optionalText,
  body: z.string().trim().min(1),
  source: optionalText,
});

export const RecommendationBlockSchema = z.object({
  type: z.literal("recommendation"),
  title: optionalText,
  body: z.string().trim().min(1),
});

export const RubricItemBlockSchema = z.object({
  type: z.literal("rubric_item"),
  label: z.string().trim().min(1),
  score,
  maxScore: z.number().int().min(1).max(100),
  note: optionalText,
});

export const ScoreComponentBlockSchema = z.object({
  type: z.literal("score_component"),
  label: z.string().trim().min(1),
  score,
  weight: z.number().int().min(0).max(100).nullable(),
  detail: optionalText,
});

export const QaBlockSchema = z.object({
  type: z.literal("qa"),
  q: z.string().trim().min(1),
  a: z.string().trim().min(1),
});

export const ContentBlockSchema = z.discriminatedUnion("type", [
  NoteBlockSchema,
  EvidenceBlockSchema,
  RecommendationBlockSchema,
  RubricItemBlockSchema,
  ScoreComponentBlockSchema,
  QaBlockSchema,
]);

export type ContentBlock = z.infer<typeof ContentBlockSchema>;

/**
 * The human-readable prose a block carries, in reading order.
 *
 * Which blocks have prose — and what it is called on each — is a property of
 * the block type, so it is answered once here, exhaustively over the union. A
 * consumer that tested for a `body` property instead silently ignored the
 * rubric, score and Q&A blocks, whose text lives under different names.
 */
export function blockText(block: ContentBlock): string[] {
  switch (block.type) {
    case BLOCK_TYPE.NOTE:
    case BLOCK_TYPE.RECOMMENDATION:
      return [block.body];
    case BLOCK_TYPE.EVIDENCE:
      return [block.body];
    case BLOCK_TYPE.RUBRIC_ITEM:
      return [block.label, ...(block.note === null ? [] : [block.note])];
    case BLOCK_TYPE.SCORE_COMPONENT:
      return [block.label, ...(block.detail === null ? [] : [block.detail])];
    case BLOCK_TYPE.QA:
      return [block.q, block.a];
  }
}
